import { SignalsDefinition, signalsTopicSegment } from './types';
import { t } from '../i18n';

/** MQTT topic segment, lowercased — `Signal 1` and `Signal1` collide. */
export function signalSlotNameKey(name: string): string {
  return signalsTopicSegment(name).toLowerCase();
}

export function signalSlotNameTaken(name: string, existingNames: string[]): boolean {
  const key = signalSlotNameKey(name);
  return existingNames.some((item) => signalSlotNameKey(item) === key);
}

export function uniqueSignalSlotName(preferred: string, existingNames: string[]): string {
  const base = String(preferred || '').trim() || t('signals.defaultSlotName', { index: 1 });
  if (!signalSlotNameTaken(base, existingNames)) {
    return base;
  }
  for (let n = 2; n < 1000; n++) {
    const candidate = `${base} ${n}`;
    if (!signalSlotNameTaken(candidate, existingNames)) {
      return candidate;
    }
  }
  return `${base} ${Date.now()}`;
}

export function nextDefaultSignalSlotName(existingNames: string[]): string {
  for (let n = 1; n < 1000; n++) {
    const candidate = t('signals.defaultSlotName', { index: n });
    if (!signalSlotNameTaken(candidate, existingNames)) {
      return candidate;
    }
  }
  return uniqueSignalSlotName(t('signals.defaultSlotName', { index: 1 }), existingNames);
}

export function uniquifySignalSlots<T extends { name: string }>(slots: T[]): T[] {
  const seen: string[] = [];
  let changed = false;
  const next = slots.map((slot) => {
    const name = slot.name.trim()
      ? uniqueSignalSlotName(slot.name, seen)
      : nextDefaultSignalSlotName(seen);
    seen.push(name);
    if (name !== slot.name) {
      changed = true;
      return { ...slot, name };
    }
    return slot;
  });
  return changed ? next : slots;
}

export function createDefaultSignals(): SignalsDefinition {
  return {
    metadata: {
      version: '1.0',
      name: t('signals.newName'),
      description: t('signals.newDescription'),
    },
    mqttTopic: '',
    signals: [],
  };
}

export function slugifySignalsName(name: string): string {
  const slug = name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  return slug || 'default';
}

export function createSignalSlot(existingNames: string[] = []) {
  const id = `signal_${Date.now()}_${existingNames.length}`;
  return {
    id,
    name: nextDefaultSignalSlotName(existingNames),
    previewStatus: undefined as string | undefined,
    statuses: [
      { id: `${id}_off`, name: t('signals.defaultStatusOff'), color: '#000000', mode: 'off' as const },
      { id: `${id}_on`, name: t('signals.defaultStatusOn'), color: '#00ff00', mode: 'steady' as const },
    ],
  };
}

export function createSignalStatus(slotId: string, index: number) {
  return {
    id: `${slotId}_status_${Date.now()}_${index}`,
    name: t('signals.defaultStatusName', { index: index + 1 }),
    color: '#ffff00',
    mode: 'steady' as const,
  };
}

export const SIGNAL_PRESET_COLORS = [
  { id: 'off', color: '#000000', labelKey: 'signals.colors.off' },
  { id: 'red', color: '#ff0000', labelKey: 'signals.colors.red' },
  { id: 'yellow', color: '#ffff00', labelKey: 'signals.colors.yellow' },
  { id: 'green', color: '#00ff00', labelKey: 'signals.colors.green' },
  { id: 'blue', color: '#0000ff', labelKey: 'signals.colors.blue' },
  { id: 'white', color: '#ffffff', labelKey: 'signals.colors.white' },
  { id: 'orange', color: '#ff8000', labelKey: 'signals.colors.orange' },
];
